import { Check, ChevronRight, Send } from 'lucide-react'
import type { OutboxEntry } from '../../lib/mail-outbox'
import { formatClock } from './offline-bar'

// Outbox line above the mail list (#785, mockup 1d `.outrow`) and the
// „gesendet HH:MM" badge for mails that left the outbox in this session.

/** „1 Mail wartet" / „3 Mails warten". */
export function outboxWaitingText(count: number): string {
  return count === 1 ? '1 Mail wartet' : `${count} Mails warten`
}

export function OutboxRow({
  entries,
  onOpen,
}: {
  entries: readonly Pick<OutboxEntry, 'status'>[]
  onOpen: () => void
}) {
  if (entries.length === 0) return null
  const failed = entries.filter((e) => e.status === 'fehler').length
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-h-11 w-full items-center gap-2.5 rounded-[14px] border border-info/35 bg-info/[0.08] px-3 py-2.5 text-left transition-colors hover:bg-info/[0.12]"
    >
      <Send className="h-[18px] w-[18px] shrink-0 text-info" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <b className="block truncate text-[13px] font-semibold text-text-primary">
          Ausgang · {outboxWaitingText(entries.length)}
        </b>
        <span className={failed > 0 ? 'text-[11px] text-danger' : 'text-[11px] text-text-tertiary'}>
          {failed > 0
            ? `${failed} nicht gesendet – bitte prüfen`
            : 'Wird gesendet, sobald du online bist'}
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
    </button>
  )
}

/** A mail sent from the outbox in this session (kept in page state only). */
export interface SentMark {
  accountId: string
  subject: string
  inReplyTo?: string
  references?: readonly string[]
  sentAt: string
}

interface MarkableThread {
  threadRoot: string
  latest: { accountId: string; subject: string | null }
  messages: ReadonlyArray<{ messageId: string | null }>
}

/** Subject without reply/forward prefixes, for a best-effort match. */
export function baseSubject(subject: string | null | undefined): string {
  let s = (subject ?? '').trim()
  for (;;) {
    const next = s.replace(/^(re|aw|fwd?|wg)\s*:\s*/i, '')
    if (next === s) break
    s = next
  }
  return s.toLowerCase()
}

/** The newest mark that belongs to this conversation: same account AND (the
 * reply headers point into the thread, or the base subject is the same). */
export function sentMarkFor(thread: MarkableThread, marks: readonly SentMark[]): SentMark | null {
  let best: SentMark | null = null
  const ids = new Set(thread.messages.map((m) => m.messageId).filter((id): id is string => !!id))
  const subject = baseSubject(thread.latest.subject)
  for (const mark of marks) {
    if (mark.accountId !== thread.latest.accountId) continue
    const refs = mark.references ?? []
    const byHeaders =
      refs.includes(thread.threadRoot) ||
      (!!mark.inReplyTo && ids.has(mark.inReplyTo)) ||
      refs.some((r) => ids.has(r))
    const bySubject = subject !== '' && baseSubject(mark.subject) === subject
    if (!byHeaders && !bySubject) continue
    if (!best || mark.sentAt > best.sentAt) best = mark
  }
  return best
}

export function SentBadge({ sentAt }: { sentAt: string }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success/15 px-1.5 text-2xs font-medium text-success">
      <Check className="h-3 w-3" aria-hidden="true" />
      gesendet {formatClock(sentAt)}
    </span>
  )
}
